import { ApiError, clearFormError, copyText, esc, formError, formValues, get, post, toast } from "../lib.js";
import { checkOrgLogos, orgTileHtml } from "../orglogo.js";
import { navigate, onAction, onForm, refreshSession, route, state } from "../state.js";
import { en, langWrap, t as inLanguage } from "../i18n.js";
import { openableWorkspaces, rememberOrg } from "../profile.js";
import { authBrandHtml } from "../authbrand.js";
import { orLine, providerButtons } from "../signin.js";
import { ssoErrorText } from "../../../shared/signin.js";

// The sign-in, two-factor and password screens are English only (approved 09/10/2026). Each person's screen language
// (profile, User Database) still applies after signing in: the QC screens and the one-time agreement screen.
const t = en;
const currentLang = () => "en";

/** The sign-in screens (sign in, two-factor, password links) are translated: the small language switch at the top is
 *  remembered on this device until the person signs in (then their profile's language wins). */
export function authCard(title: string, subtitle: string, body: string, wide = false, translated = false): void {
  const inner = `<div class="falcon-auth-body">
      <h1>${esc(title)}</h1>
      ${subtitle ? `<p class="falcon-auth-sub">${subtitle}</p>` : ""}
      ${body}
    </div>`;
  document.getElementById("root")!.innerHTML = `
<div class="falcon-auth-page">
  <div class="falcon-auth-card ${wide ? "wide" : ""}">
    ${authBrandHtml("")}
    ${translated ? langWrap(inner) : inner}
  </div>
  <p class="falcon-auth-foot">Falcon Platform · V${esc(state.session.version)}${state.session.build ? ` (${esc(state.session.build)})` : ""}</p>
</div>`;
  document.title = `FALCON PLM · ${title}`;
  document.querySelector<HTMLInputElement>(".falcon-auth-card input:not([type=hidden]):not([readonly])")?.focus();
}

const msg = `<div class="form-message" role="alert" hidden></div>`;
/** A server message in the screen's language when it has one (by its code); otherwise as the server sent it. */
function authError(err: unknown): unknown {
  if (currentLang() === "en" || !(err instanceof ApiError)) return err;
  const k = `auth.err.${err.code}`, tr = t(k);
  return tr ? new ApiError(err.status, err.code, tr, err.field, err.details) : err;
}

async function busy(form: HTMLFormElement, fn: () => Promise<void>): Promise<void> {
  const btn = form.querySelector<HTMLButtonElement>("button[type=submit],button.primary");
  const label = btn?.textContent;
  clearFormError(form);
  if (btn) { btn.disabled = true; btn.textContent = t("auth.wait"); }
  try {
    await fn();
  } catch (err) {
    formError(form, authError(err));
  } finally {
    if (btn && btn.isConnected) { btn.disabled = false; btn.textContent = label ?? ""; }
  }
}

/** A password field with a "Show" button that shows the typed password (and hides it again). */
function passwordBox(id: string, name: string, autocomplete: string, extra = ""): string {
  return `<div class="falcon-password-box"><input id="${id}" name="${name}" type="password" autocomplete="${autocomplete}" dir="ltr" required${extra}><button type="button" class="falcon-password-show" data-action="show-password" aria-controls="${id}" aria-pressed="false">Show</button></div>`;
}
onAction("show-password", (el) => {
  const input = document.getElementById(el.getAttribute("aria-controls") || "") as HTMLInputElement | null;
  if (!input) return;
  const show = input.type === "password";
  input.type = show ? "text" : "password";
  el.textContent = show ? "Hide" : "Show";
  el.setAttribute("aria-pressed", String(show));
  input.focus();
});

// A message from signing in with Google, Microsoft or Apple (shown once, on the page the person comes back to).
let ssoNotice = "";
const takeSsoNotice = () => { const n = ssoNotice; ssoNotice = ""; return n ? `<div class="form-message error" role="alert">${esc(n)}</div>` : ""; };

// ---- Sign in ------------------------------------------------------------------
route("/login", ({ query }) => {
  const email = query.get("email") || "";
  const note = query.get("done") === "password" ? `<div class="form-message ok">${esc(t("auth.passwordSaved"))}</div>` : query.get("expired") ? `<div class="form-message">${esc(t("auth.sessionEnded"))}</div>` : "";
  const sso = providerButtons("Continue", "signin");
  authCard(t("auth.signIn"), esc(sso ? "Use the account your organisation registered for you." : t("auth.signInSub")), `
<form data-form="login" class="falcon-auth-form" novalidate>
  ${note}${takeSsoNotice()}${sso ? `${sso}${orLine("or sign in with your email and password")}` : ""}${msg}
  <div class="form-group"><label for="login-email">${esc(t("auth.email"))}</label><input id="login-email" name="email" type="email" autocomplete="username" dir="ltr" required value="${esc(email)}"></div>
  <div class="form-group"><label for="login-password">${esc(t("auth.password"))}</label>${passwordBox("login-password", "password", "current-password")}</div>
  <button type="submit" class="primary falcon-auth-submit">${esc(t("auth.signIn"))}</button>
  <a class="falcon-auth-link" href="/forgot-password" data-link>${esc(t("auth.forgotLink"))}</a>
</form>`);
  if (email) document.getElementById("login-password")?.focus();
});

onForm("login", (form) => busy(form, async () => {
  const v = formValues(form);
  if (!v.email || !v.password) throw new Error(t("auth.enterEmailPassword"));
  const r = await post<{ next: string }>("/api/auth/login", { email: v.email, password: v.password });
  await refreshSession(true);
  await navigate(r.next === "mfa" ? "/login/verify" : "/", true);
}));

// ---- Two-factor code ---------------------------------------------------------------
route("/login/verify", ({ query }) => {
  const recovery = query.get("recovery") === "1";
  authCard(t("auth.twoFactorCheck"), esc(recovery ? t("auth.recoverySub") : t("auth.codeSub")), `
<form data-form="verify" class="falcon-auth-form" novalidate>
  ${msg}
  ${recovery
    ? `<div class="form-group"><label for="recovery-code">${esc(t("auth.recoveryCode"))}</label><input id="recovery-code" name="recoveryCode" autocomplete="one-time-code" placeholder="XXXXX-XXXXX" dir="ltr" required></div>`
    : `<div class="form-group"><label for="mfa-code">${esc(t("auth.sixDigit"))}</label><input id="mfa-code" class="falcon-code-input" name="code" inputmode="numeric" pattern="[0-9]*" maxlength="6" autocomplete="one-time-code" dir="ltr" required></div>`}
  <button type="submit" class="primary falcon-auth-submit">${esc(t("auth.continue"))}</button>
  <a class="falcon-auth-link" href="/login/verify${recovery ? "" : "?recovery=1"}" data-link>${esc(recovery ? t("auth.useApp") : t("auth.useRecovery"))}</a>
  <button type="button" class="link-btn falcon-auth-link" data-action="sign-out">${esc(t("auth.cancelSignOut"))}</button>
</form>`);
});

onForm("verify", (form) => busy(form, async () => {
  const v = formValues(form);
  const r = await post<{ next: string; recoveryCodesLeft?: number }>("/api/auth/mfa", v.recoveryCode ? { recoveryCode: v.recoveryCode } : { code: String(v.code || "").trim() });
  if (r.recoveryCodesLeft !== undefined) toast(t("auth.recoveryAccepted", { n: r.recoveryCodesLeft }));
  await refreshSession(true);
  await navigate("/", true);
}));

// ---- Forgotten password ---------------------------------------------------------------
route("/forgot-password", () => {
  authCard(t("auth.resetTitle"), esc(t("auth.resetSub")), `
<form data-form="forgot" class="falcon-auth-form" novalidate>
  ${msg}
  <div class="form-group"><label for="forgot-email">${esc(t("auth.email"))}</label><input id="forgot-email" name="email" type="email" autocomplete="username" dir="ltr" required></div>
  <button type="submit" class="primary falcon-auth-submit">${esc(t("auth.sendReset"))}</button>
  <a class="falcon-auth-link" href="/login" data-link>${esc(t("auth.backToSignIn"))}</a>
</form>`);
});

onForm("forgot", (form) => busy(form, async () => {
  const r = await post<{ message: string }>("/api/auth/forgot", formValues(form));
  form.innerHTML = `<div class="form-message ok">${esc(currentLang() === "en" ? r.message : t("auth.resetSent"))}</div><a class="falcon-auth-link" href="/login" data-link>${esc(t("auth.backToSignIn"))}</a>`;
}));

// ---- Choose a password (invitation or reset link) ----------------------------------
async function passwordPage(query: URLSearchParams, kind: "invite" | "reset"): Promise<void> {
  const token = query.get("token") || "";
  let info: { purpose: string; email: string; firstName: string };
  try {
    info = await get(`/api/auth/token?token=${encodeURIComponent(token)}`);
  } catch (err) {
    authCard(t("auth.linkUnusable"), "", `<div class="form-message error">${esc((authError(err) as Error).message)}</div>
      <a class="falcon-auth-link" href="/forgot-password" data-link>${esc(t("auth.requestNewLink"))}</a><a class="falcon-auth-link" href="/login" data-link>${esc(t("auth.backToSignIn"))}</a>`);
    return;
  }
  const invite = info.purpose === "invite";
  const otherUser = state.session.signedIn ? state.session.user?.email || "another account" : "";
  // An invitation can be accepted with Google, Microsoft or Apple instead of a password (when switched on).
  const sso = invite ? providerButtons("Accept", "invite", { token }) : "";
  authCard(invite ? (info.firstName ? t("auth.welcomeName", { name: info.firstName }) : t("auth.welcome")) : t("auth.chooseNew"),
    esc(invite ? (sso ? "Choose how you'll sign in to Falcon." : t("auth.inviteSub")) : t("auth.resetPasswordSub")), `
<form data-form="set-password" class="falcon-auth-form" novalidate>
  ${otherUser && otherUser !== info.email ? `<div class="form-message">${t("auth.otherUser", { other: `<strong>${esc(otherUser)}</strong>`, email: esc(info.email) })}</div>` : ""}
  ${invite ? takeSsoNotice() : ""}
  ${sso ? `<div class="falcon-auth-sso-invited"><span class="falcon-auth-sso-invited-email" dir="ltr">${esc(info.email)}</span><small>The address you were invited with</small></div>
  ${sso}<p class="falcon-auth-sso-note">No password needed. Next time, press the same button on the sign-in page.</p>${orLine("or choose a password instead")}` : ""}
  ${msg}
  <input type="hidden" name="token" value="${esc(token)}">
  ${sso ? "" : `<div class="form-group"><label>${esc(t("auth.email"))}</label><input value="${esc(info.email)}" readonly autocomplete="username" dir="ltr"></div>`}
  <div class="form-group"><label for="new-password">${esc(t("auth.newPassword"))}</label>${passwordBox("new-password", "password", "new-password", ' minlength="10"')}<small>${esc(t("auth.passwordHint"))}</small></div>
  <div class="form-group"><label for="confirm-password">${esc(t("auth.typeAgain"))}</label><input id="confirm-password" name="confirm" type="password" autocomplete="new-password" dir="ltr" required></div>
  <button type="submit" class="primary falcon-auth-submit">${esc(invite ? t("auth.activate") : t("auth.saveNewPassword"))}</button>
</form>`);
  void kind;
}
route("/accept-invite", ({ query }) => passwordPage(query, "invite"));

// ---- Back from Google, Microsoft or Apple ---------------------------------------------------------
route("/sso-return", async ({ query }) => {
  const provider = query.get("provider") || "";
  const body = { provider, code: query.get("code") || undefined, state: query.get("state") || undefined, error: query.get("error") || undefined };
  // The one-time code isn't left in the address bar or the browser's history.
  history.replaceState(null, "", "/sso-return");
  authCard("Signing you in…", "", `<div class="falcon-auth-form"><p class="falcon-auth-sso-note">${esc(t("auth.wait"))}</p></div>`);
  // A button that was switched off before the person pressed it: straight back, with the message.
  if (body.error && !body.state) {
    return goBack(query.get("back") || "/login", ssoErrorText(body.error, provider));
  }
  try {
    const r = await post<{ next: string; back: string; settingsUnlockedUntil?: string }>("/api/auth/sso/finish", body);
    if (r.next === "unlocked") {
      state.session.settingsUnlockedUntil = r.settingsUnlockedUntil;
      toast("Settings unlocked for 15 minutes.");
    } else if (r.next === "linked") {
      toast("Linked. You can now sign in with it.");
    }
    await refreshSession(true);
    await navigate(r.next === "mfa" ? "/login/verify" : r.back || "/", true);
  } catch (err) {
    const back = err instanceof ApiError && (err.details as any)?.back ? String((err.details as any).back) : "/login";
    await goBack(back, (err as Error).message);
  }
});
async function goBack(to: string, message: string): Promise<void> {
  // Only Falcon's own pages.
  const back = /^\/(?![/\\])/.test(to) ? to : "/login";
  const page = back.split("?")[0];
  if (page === "/login" || page === "/accept-invite") ssoNotice = message;
  else toast(message, "error");
  await refreshSession(true).catch(() => {});
  await navigate(back, true);
}

route("/reset-password", ({ query }) => passwordPage(query, "reset"));

onForm("set-password", (form) => busy(form, async () => {
  const v = formValues(form);
  if (v.password !== v.confirm) throw new Error(t("auth.noMatch"));
  const r = await post<{ email: string }>("/api/auth/set-password", { token: v.token, password: v.password });
  await refreshSession(true);
  await navigate(`/login?done=password&email=${encodeURIComponent(r.email)}`, true);
}));

// ---- Two-factor set-up (compulsory for Admins and the platform owner) ----------------
export async function twoFactorSetupHtml(context: "required" | "account"): Promise<string> {
  const r = await post<{ secret: string; qrDataUrl: string }>("/api/account/2fa/start");
  // Translated on the sign-in screens; My Account stays English.
  const tr = context === "required" ? t : en;
  return `
<div class="falcon-2fa-steps">
  ${context === "required" ? `<div class="form-message">${esc(tr("auth.2fa.required"))}</div>` : ""}
  <ol>
    <li>${tr("auth.2fa.step1", { b: "<strong>", eb: "</strong>" })}</li>
    <li>${tr("auth.2fa.step2", { b: "<strong>", eb: "</strong>" })}
      <div class="falcon-qr"><img src="${esc(r.qrDataUrl)}" alt="${esc(tr("auth.2fa.qrAlt"))}" width="180" height="180">
      <small>${esc(tr("auth.2fa.cantScan"))}<br><code class="falcon-secret" dir="ltr">${esc(r.secret.replace(/(.{4})/g, "$1 ").trim())}</code></small></div></li>
    <li>${tr("auth.2fa.step3", { b: "<strong>", eb: "</strong>" })}</li>
  </ol>
  <form data-form="confirm-2fa" data-context="${context}" class="falcon-auth-form" novalidate>
    ${msg}
    <div class="form-group"><label for="setup-code">${esc(tr("auth.sixDigit"))}</label><input id="setup-code" class="falcon-code-input" name="code" inputmode="numeric" maxlength="6" autocomplete="one-time-code" dir="ltr" required></div>
    <button type="submit" class="primary falcon-auth-submit">${esc(tr("auth.2fa.turnOn"))}</button>
  </form>
</div>`;
}

export function recoveryCodesHtml(codes: string[], continueHref: string, translated = false): string {
  const tr = translated ? t : en;
  return `
<div class="falcon-recovery">
  <div class="form-message ok">${esc(tr("auth.2fa.on"))}</div>
  <p>${tr("auth.2fa.saveCodes", { b: "<strong>", eb: "</strong>" })}</p>
  <div class="falcon-recovery-codes" dir="ltr">${codes.map((c) => `<code>${esc(c)}</code>`).join("")}</div>
  <div class="settings-button-row">
    <button type="button" class="secondary" data-action="copy-codes" data-codes="${esc(codes.join("\n"))}">${esc(tr("auth.2fa.copy"))}</button>
    <button type="button" class="secondary" data-action="download-codes" data-codes="${esc(codes.join("\n"))}">${esc(tr("auth.2fa.download"))}</button>
    <a class="primary falcon-continue" href="${esc(continueHref)}" data-link data-action="after-2fa">${esc(tr("auth.2fa.saved"))}</a>
  </div>
</div>`;
}

onAction("copy-codes", (el) => copyText(el.dataset.codes || ""));
onAction("download-codes", (el) => {
  const blob = new Blob([`Falcon PLM recovery codes for ${state.session.user?.email}\nEach code works once.\n\n${el.dataset.codes}\n`], { type: "text/plain" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "falcon-recovery-codes.txt";
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
});
onAction("after-2fa", async (_el, ev) => {
  ev.preventDefault();
  await refreshSession();
  await navigate(state.session.mode === "platform" ? "/console/account" : state.session.mode === "organisation" ? "/app/account" : "/", true);
});

route("/setup-two-factor", async () => {
  authCard(t("auth.2fa.title"), "", `<div id="twofa-holder">${esc(t("auth.loading"))}</div>`, true);
  try {
    document.getElementById("twofa-holder")!.innerHTML = await twoFactorSetupHtml("required");
    document.getElementById("setup-code")?.focus();
  } catch (err) {
    document.getElementById("twofa-holder")!.innerHTML = `<div class="form-message error">${esc((err as Error).message)}</div>`;
  }
});

onForm("confirm-2fa", (form) => busy(form, async () => {
  const r = await post<{ recoveryCodes: string[] }>("/api/account/2fa/confirm", { code: String(formValues(form).code || "").trim() });
  const holder = form.closest(".falcon-2fa-steps")!;
  holder.innerHTML = recoveryCodesHtml(r.recoveryCodes, "/", (form as HTMLElement).dataset.context === "required");
}));

// ---- Choose an organisation (people in two or more; the Falcon owner also sees the Console) -----------------------
route("/workspaces", () => {
  const s = state.session;
  const list = openableWorkspaces();
  const logo = (w: { name: string; logoUrl?: string }) => orgTileHtml(w.name, w.logoUrl);
  const cards = [
    ...(s.user?.isPlatformOwner ? [`<button type="button" class="falcon-workspace-card platform" data-action="choose-platform"><span class="falcon-org-initials">F</span><div><strong>Falcon Admin Console</strong><small>Organisations, licences and billing</small></div></button>`] : []),
    ...list.map((w) => `<button type="button" class="falcon-workspace-card" data-action="choose-workspace" data-org="${esc(w.orgId)}">${logo(w)}<div><strong>${esc(w.name)}</strong><small>${esc(w.role)}${w.closed ? " · closed: read-only" : ""}</small></div></button>`),
  ];
  const remember = !s.user?.isPlatformOwner && list.length > 1;
  authCard(s.user?.isPlatformOwner ? "Choose a workspace" : "Choose an organisation", s.user?.isPlatformOwner ? "Your account can open more than one area." : "You work in more than one organisation. Choose one to open; you can switch at any time from your name at the bottom of the menu.", `
<div class="falcon-workspace-list">${cards.join("") || `<div class="form-message">Your account isn't linked to an organisation yet. Ask your organisation's Admin to add you.</div>`}</div>
${remember ? `<label class="falcon-check falcon-remember-org"><input type="checkbox" id="remember-org" checked> Remember my choice on this device</label>` : ""}
<button type="button" class="link-btn falcon-auth-link" data-action="sign-out">Sign out</button>`, true);
  checkOrgLogos();
});

onAction("choose-workspace", async (el) => {
  try {
    const orgId = el.dataset.org || "";
    await post("/api/session/workspace", { orgId });
    const box = document.getElementById("remember-org") as HTMLInputElement | null;
    rememberOrg(box && !box.checked ? null : orgId);
    await refreshSession();
    await navigate("/app", true);
  } catch (err) {
    toast((err as Error).message, "error");
  }
});
onAction("choose-platform", async () => {
  await post("/api/session/workspace", { mode: "platform" });
  await refreshSession();
  await navigate("/console", true);
});
onAction("sign-out", async () => {
  await post("/api/auth/logout").catch(() => {});
  await refreshSession(true);
  await navigate("/login", true);
});
